import { useOptimizedDocuments, FamilyDocument, CreateDocumentData } from './useOptimizedDocuments';
import { useFamily } from '@/hooks/useFamily';

/**
 * Wrapper around useOptimizedDocuments to maintain compatibility with legacy useDocuments interface
 * This allows existing components to gradually migrate to the optimized version
 */
export function useDocuments(familyId?: string) {
  const { family } = useFamily();
  const effectiveFamilyId = familyId || family?.id;

  const {
    documents,
    loading,
    createDocument: optimizedCreateDocument,
    createDocumentAsync: optimizedCreateDocumentAsync,
    updateDocument: optimizedUpdateDocument,
    updateDocumentAsync: optimizedUpdateDocumentAsync,
    deleteDocument: optimizedDeleteDocument,
    deleteDocumentAsync: optimizedDeleteDocumentAsync,
    uploadFile: optimizedUploadFile,
    isCreating,
    isUpdating,
    isDeleting,
    isUploading,
    creating,
    updating,
    uploading,
    refreshDocuments,
  } = useOptimizedDocuments(effectiveFamilyId);

  // Convert optimized functions to match legacy interface  
  const createDocument = async (documentData: CreateDocumentData) => {
    return optimizedCreateDocumentAsync(documentData);
  };

  const updateDocument = async (documentId: string, updates: Partial<CreateDocumentData>) => {
    return optimizedUpdateDocumentAsync({ documentId, updates });
  };

  const deleteDocument = async (documentId: string) => {
    return new Promise<void>((resolve) => {
      optimizedDeleteDocument(documentId);
      resolve();
    });
  };

  const uploadFile = async (file: File) => {
    return optimizedUploadFile(file);
  };

  return {
    documents,
    loading,
    createDocument,
    updateDocument,
    deleteDocument,
    uploadFile,
    isCreating,
    isUpdating,
    isDeleting,
    isUploading,
    // Legacy properties
    creating,
    updating,
    uploading,
    refreshDocuments,
  };
}

// Re-export the types for compatibility
export type { FamilyDocument, CreateDocumentData };